import { type Key, useEffect, useState } from 'react';

import './Edit.styles.css';
import { DatePicker, Form, Input, Tabs, TreeSelect, Upload } from 'antd';
import { Formik } from 'formik';
import { InboxOutlined } from '@ant-design/icons';
import type { TopicType } from '../../../topics/Topics.types.ts';
import { useParams } from 'react-router-dom';
import { HTMLEditor } from '../../../../components/html-editor';
import { FileUploadView } from '../../../../components/file-upload-view';
import { FiPlus } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { buildTree, buildTreeByAbbreviation, getParentTopic } from '../../../../helpers';
import { TreeModal } from '../../../../components/tree-model';
import { LinkModal } from '../links-modal';
import { getDraftValues, normalize } from '../../Draft.helpers.ts';
import type { DraftFormValues } from '../../Draft.types.ts';
import { useService } from '../../Draft.hooks.ts';
import { useService as useTopicsService } from '../../../topics/Topics.hooks.ts';
import { useService as useUnitsService } from '../../../organization-unit-lists/OrganizationUnitLists.hooks.ts';
import { LinkView } from '../../../../components/link-view';
import { VALIDATION_SCHEMA } from '../../Draft.constants.tsx';
import { REGULATION_TYPE } from '../../../../constants/regulation-type.tsx';
import { Tag } from '../tag';
import { DebounceSelect } from '../../../../components/debonce-select';
import { searchLeadership } from '../../../organization-unit-lists/OrganizationUnitLists.services.ts';

const { Dragger } = Upload;

const DATE_FORMAT = 'DD.MM.YYYY';

interface FileWithUid extends File {
  uid: string;
}

function AutoTabSwitcher({
  isSubmitting,
  errors,
  setActiveKey,
}: {
  isSubmitting: boolean;
  errors: Record<string, unknown>;
  setActiveKey: (k: string) => void;
}) {
  useEffect(() => {
    if (!isSubmitting) return;

    const keys = Object.keys(errors);
    const targetKeys = [
      'title',
      'creationDate',
      'topics',
      'unitInChargeId',
      'author',
    ];
    const hasAny = targetKeys.some((k) => keys.includes(k));

    if (keys.length > 0 && hasAny) {
      setActiveKey('1');
      return;
    }
    if (keys.includes('shortDescription')) {
      setActiveKey('2');
    }
  }, [isSubmitting, errors, setActiveKey]);

  return null;
}

export default function Edit({
  onSubmit,
  regType,
}: {
  onSubmit: (values: DraftFormValues) => void;
  regType: number;
}) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState<Key[]>([]);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState<Key[]>([]);
  const [activeKey, setActiveKey] = useState('1');

  const { regulation } = useService({ regulationId: Number(id) });
  const { topics } = useTopicsService({ topicId: null });
  const { units } = useUnitsService({ organizationUnitListId: null });

  const unitsTree = units
    ? buildTreeByAbbreviation(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        units.map((unit: { id: number }) => ({
          ...unit,
          key: unit.id.toString(),
        })),
      )
    : [];

  const topicsTree = buildTree(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    topics?.map((topic: TopicType) => ({
      ...topic,
      key: topic.id,
      parentId: topic.parentTopicId,
      title: topic.name,
    })),
  );

  const data = normalize(regulation, regType);
  const initialValues = getDraftValues(data);

  const handleOpenUnitsModal = () => setShowUnitsModal(true);
  const handleCloseUnitsModal = () => setShowUnitsModal(false);
  const handleOpenTopicsModal = () => setShowTopicsModal(true);
  const handleCloseTopicsModal = () => setShowTopicsModal(false);
  const handleOpenLinksModal = () => setShowLinksModal(true);
  const handleCloseLinksModal = () => setShowLinksModal(false);

  useEffect(() => {
    document.title = `Entwurf ${data?.title ? ' | ' + data?.title : ''}`;
  }, [data]);

  return (
    <div className="content-container">
      <Formik
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        initialValues={initialValues}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, handleChange, values, errors, touched, setFieldValue, isSubmitting }) => {
          const handleDeleteFile = (uid: string) => {
            setFieldValue(
              'attachments',
              /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              /* @ts-ignore @ts-expect-error */
              values.attachments.filter((file: FileWithUid) => file.uid !== uid),
            );
          };

          const handleDeleteUnitInCharge = () => {
            setFieldValue('unitInChargeId', null);
          };

          const handleDeleteUnit = (uid: number | string) => {
            setFieldValue(
              'involvedUnits',
              values?.involvedUnits?.filter((u: number | string) => Number(u) !== Number(uid)),
            );
          };

          const handleDeleteTopic = (uid: number | string) => {
            setFieldValue(
              'topics',
              values.topics.filter((u: number | string) => u !== uid),
            );
          };

          const handleSubmitLinks = (link: { title: string; url: string }) => {
            setFieldValue('links', [...values.links, link]);
            handleCloseLinksModal();
          };

          const handleDeleteLink = (title: string) => {
            setFieldValue('links', [
              ...values.links.filter(
                (link: { title: string; url: string }) => link.title !== title,
              ),
            ]);
            handleCloseLinksModal();
          };

          const Properties = () => <span />;
          const Content = () => <span />;
          const Attachments = () => <span />;

          const onChange = (key: string) => {
            setActiveKey(key);
          };

          const tabs = [
            {
              key: '1',
              label: t('tab.properties'),
              children: <Properties />,
            },
            {
              key: '2',
              label: t('tab.content'),
              children: <Content />,
            },
            {
              key: '3',
              label: t('tab.attachments'),
              children: <Attachments />,
            },
          ];
          const confirmationPeriodLabel =
            values.type == null
              ? ''
              : t(
                  values.type === 2
                    ? 'form.option.thirty-six-months'
                    : 'form.option.eighteen-months',
                );
          return (
            <>
              <AutoTabSwitcher
                isSubmitting={isSubmitting}
                errors={errors}
                setActiveKey={setActiveKey}
              />
              <Form
                layout="vertical"
                onFinish={handleSubmit}
                id="DraftRegulationForm"
                style={{ width: '100%' }}
              >
                <Form.Item
                  label={t('form.field.title')}
                  validateStatus={touched.title && errors.title ? 'error' : ''}
                  help={
                    touched.title && typeof errors.title === 'string' ? errors.title : undefined
                  }
                >
                  <Input
                    width="100%"
                    name="title"
                    variant="outlined"
                    value={values.title}
                    onChange={handleChange}
                    placeholder={t('form.placeholder.type-here')}
                  />
                </Form.Item>
                <Form.Item
                  label={t('form.field.title-english')}
                  validateStatus={touched.titleEnglish && errors.titleEnglish ? 'error' : ''}
                  help={
                    touched.titleEnglish && typeof errors.titleEnglish === 'string'
                      ? errors.titleEnglish
                      : undefined
                  }
                >
                  <Input
                    width="100%"
                    name="titleEnglish"
                    variant="outlined"
                    value={values.titleEnglish}
                    onChange={handleChange}
                    placeholder={t('form.placeholder.type-here')}
                  />
                </Form.Item>
                <Tabs
                  defaultActiveKey="1"
                  centered
                  activeKey={activeKey}
                  style={{ marginTop: 16, color: '#98cb00' }}
                  items={tabs}
                  onChange={onChange}
                />
                {activeKey === '1' && (
                  <>
                    <Form.Item
                      label={t('form.field.type')}
                      validateStatus={touched.type && errors.type ? 'error' : ''}
                    >
                      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                      {/* @ts-expect-error */}
                      {REGULATION_TYPE[values.type] as 1 | 2 | 3}
                    </Form.Item>
                    <Form.Item
                      label={t('form.field.unit-in-charge')}
                      validateStatus={
                        touched.unitInChargeId && errors.unitInChargeId ? 'error' : ''
                      }
                      help={
                        touched.unitInChargeId && typeof errors.unitInChargeId === 'string'
                          ? errors.unitInChargeId
                          : undefined
                      }
                    >
                      <TreeSelect
                        showSearch
                        treeNodeFilterProp="title"
                        variant="outlined"
                        placeholder={t('form.placeholder.select-here')}
                        value={values.unitInChargeId}
                        treeCheckable
                        allowClear
                        onDeselect={handleDeleteUnitInCharge}
                        onSelect={(e) => {
                          setFieldValue('unitInChargeId', e);
                        }}
                        showCheckedStrategy={TreeSelect.SHOW_PARENT}
                        treeData={unitsTree}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('form.field.involved-units')}
                      validateStatus={touched.involvedUnits && errors.involvedUnits ? 'error' : ''}
                      help={
                        touched.involvedUnits && typeof errors.involvedUnits === 'string'
                          ? errors.involvedUnits
                          : undefined
                      }
                    >
                      <div className="tags-container">
                        {values?.involvedUnits?.map((unit: number) => {
                          const title =
                            units?.find((u: { id: number }) => u.id === Number(unit))?.title || '';
                          return (
                            <Tag key={unit} id={unit} title={title} onDelete={handleDeleteUnit} />
                          );
                        })}
                      </div>
                      <div onClick={handleOpenUnitsModal} className="add-link">
                        {t('common.add')} <FiPlus />
                      </div>
                    </Form.Item>

                    <Form.Item
                      label={t('form.field.creation-date')}
                      validateStatus={touched.creationDate && errors.creationDate ? 'error' : ''}
                      help={
                        touched.creationDate && typeof errors.creationDate === 'string'
                          ? errors.creationDate
                          : undefined
                      }
                    >
                      <div style={{ position: 'relative' }}>
                        <DatePicker
                          name="creationDate"
                          format={DATE_FORMAT}
                          placeholder={t('form.placeholder.select-here')}
                          style={{ width: '100%' }}
                          showToday={false}
                          value={values.creationDate}
                          onChange={(date) => setFieldValue('creationDate', date)}
                        />
                      </div>
                    </Form.Item>

                    <Form.Item
                      label={t('form.field.author')}
                      validateStatus={touched.author && errors.author ? 'error' : ''}
                      help={
                        touched.author && typeof errors.author === 'string'
                          ? errors.author
                          : undefined
                      }
                    >
                      <DebounceSelect
                        mode="multiple"
                        value={values.author}
                        placeholder={t('form.placeholder.type-here')}
                        fetchOptions={(term) => searchLeadership(term)}
                        style={{ width: '100%' }}
                        onChange={(newValue) => {
                          if (Array.isArray(newValue)) {
                            setFieldValue('author', newValue);
                          }
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label={t('form.field.emerged-out')}
                      validateStatus={touched.emergedOut && errors.emergedOut ? 'error' : ''}
                      help={
                        touched.emergedOut && typeof errors.emergedOut === 'string'
                          ? errors.emergedOut
                          : undefined
                      }
                    >
                      <Input
                        width="100%"
                        name="emergedOut"
                        variant="outlined"
                        value={values.emergedOut}
                        onChange={handleChange}
                        placeholder={t('form.placeholder.type-here')}
                      />
                    </Form.Item>

                    <Form.Item
                      label={t('form.field.topic')}
                      validateStatus={touched.topics && errors.topics ? 'error' : ''}
                      help={
                        touched.topics && typeof errors.topics === 'string'
                          ? errors.topics
                          : undefined
                      }
                    >
                      <div className="tags-container-column">
                        {values.topics?.map((topic: string) => {
                          const node = topics?.find((tc: { id: string }) => tc.id === topic);
                          const nodes = getParentTopic(node, [], topics);
                          const parentsNames = nodes
                            .reverse()
                            .map((n: { name: string }) => n.name)
                            .join('  >  ');
                          const title = `${parentsNames}  >  ${node?.name}`;
                          return (
                            <div>
                              <Tag
                                key={topic}
                                id={topic}
                                title={title}
                                onDelete={handleDeleteTopic}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div onClick={handleOpenTopicsModal} className="add-link">
                        {t('common.add')} <FiPlus />
                      </div>
                    </Form.Item>

                    <Form.Item label={t('form.field.confirmation-period')}>
                      {confirmationPeriodLabel}
                    </Form.Item>

                    {/*<Form.Item*/}
                    {/*  label={t('form.field.audience')}*/}
                    {/*  validateStatus={touched.targetGroupId && errors.targetGroupId ? 'error' : ''}*/}
                    {/*  help={*/}
                    {/*    touched.targetGroupId && typeof errors.targetGroupId === 'string'*/}
                    {/*      ? errors.targetGroupId*/}
                    {/*      : undefined*/}
                    {/*  }*/}
                    {/*>*/}
                    {/*  <Select*/}
                    {/*    variant="outlined"*/}
                    {/*    placeholder={t('form.placeholder.select-here')}*/}
                    {/*    value={values.targetGroupId}*/}
                    {/*    onChange={(e) => setFieldValue('targetGroupId', e)}*/}
                    {/*    options={groups?.map((group: TargetGroupType) => ({*/}
                    {/*      value: group.id,*/}
                    {/*      label: group.name,*/}
                    {/*    }))}*/}
                    {/*  />*/}
                    {/*</Form.Item>*/}
                  </>
                )}
                {activeKey === '2' && (
                  <>
                    <Form.Item
                      label={t('form.field.short-description')}
                      validateStatus={
                        touched.shortDescription && errors.shortDescription ? 'error' : ''
                      }
                      help={
                        touched.shortDescription && typeof errors.shortDescription === 'string'
                          ? errors.shortDescription
                          : undefined
                      }
                    >
                      <HTMLEditor
                        id="shortDescription"
                        name="shortDescription"
                        size="small"
                        readOnly={false}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('form.field.short-description-english')}
                      validateStatus={
                        touched.shortDescriptionEnglish && errors.shortDescriptionEnglish
                          ? 'error'
                          : ''
                      }
                      help={
                        touched.shortDescriptionEnglish &&
                        typeof errors.shortDescriptionEnglish === 'string'
                          ? errors.shortDescriptionEnglish
                          : undefined
                      }
                    >
                      <HTMLEditor
                        id="shortDescriptionEnglish"
                        name="shortDescriptionEnglish"
                        size="small"
                        readOnly={false}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('form.field.body-template')}
                      validateStatus={touched.content && errors.content ? 'error' : ''}
                      help={
                        touched.content && typeof errors.content === 'string'
                          ? errors.content
                          : undefined
                      }
                    >
                      <HTMLEditor id="content" name="content" size="large" readOnly={false} />
                    </Form.Item>
                  </>
                )}
                {activeKey === '3' && (
                  <>
                    <Form.Item label={t('form.field.links')}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 16,
                          marginBottom: 20,
                        }}
                      >
                        {values.links?.map((link: { title: string; url: string }) => (
                          <LinkView key={link.title} link={link} onDelete={handleDeleteLink} />
                        ))}
                      </div>
                      <div onClick={handleOpenLinksModal} className="add-link">
                        {t('common.add')} <FiPlus />
                      </div>
                    </Form.Item>
                    <Form.Item label={t('form.field.attachments')}>
                      <Dragger
                        showUploadList={false}
                        beforeUpload={(file) => {
                          setFieldValue('attachments', [...values.attachments, file]);
                          return false;
                        }}
                        onRemove={(file) => {
                          setFieldValue(
                            'attachments',
                            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                            /* @ts-ignore @ts-expect-error */
                            values.attachments.filter((f: { uid: string }) => f.uid !== file.uid),
                          );
                        }}
                        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                        /* @ts-ignore @ts-expect-error */
                        fileList={values.attachments}
                        multiple
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">{t('common.upload-text')}</p>
                        <p className="ant-upload-hint">{t('common.upload-hint')}</p>
                      </Dragger>
                    </Form.Item>
                    {values?.attachments?.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 16,
                          marginBottom: 20,
                        }}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        {values.attachments?.map((item: any) => (
                          <FileUploadView
                            showDelete
                            key={item.uid}
                            file={item}
                            onDelete={handleDeleteFile}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
                {showUnitsModal && (
                  <TreeModal
                    title={t('form.field.involved-units')}
                    open={showUnitsModal}
                    selected={values.involvedUnits}
                    expandedKeys={expandedUnits}
                    onExpand={setExpandedUnits}
                    treeData={unitsTree}
                    onClose={handleCloseUnitsModal}
                    onSubmit={(val) => setFieldValue('involvedUnits', val)}
                  />
                )}
                {showTopicsModal && (
                  <TreeModal
                    title={t('form.field.topic')}
                    open={showTopicsModal}
                    selected={values.topics}
                    expandedKeys={expandedTopics}
                    onExpand={setExpandedTopics}
                    treeData={topicsTree}
                    onClose={handleCloseTopicsModal}
                    onSubmit={(val) => setFieldValue('topics', val)}
                  />
                )}
                {showLinksModal && (
                  <LinkModal
                    open={showLinksModal}
                    onClose={handleCloseLinksModal}
                    onSubmit={handleSubmitLinks}
                  />
                )}
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
}
